// PostMessage bridge from the embeddable RPilot chat to a parent page that
// hosts it inside an <iframe>. Vue-to-Vue consumers do not need this — they
// can read events from the widget directly. iframe deployments do, because
// the host page lives in a separate JavaScript context.
//
// Wire format:
//   { source, type, id, action, payload, error }
//   - source: clientSource ('rpilot' by default) for outgoing, hostSource
//     ('host' by default) for incoming
//   - type: 'request' (child→parent, expects 'response'), 'response', or
//     'event' (one-way, fire-and-forget)
//
// Activation:
//   The bridge is inert unless the plugin is installed with a `bridge` config
//   object. Set `bridge: { enabled: 'auto' }` to activate when the widget
//   is loaded in an iframe, or `bridge: { enabled: true }` to force on.
//
// Security:
//   Every incoming message origin is validated against the configured
//   allowlist. Outgoing messages target the resolved parent origin only —
//   never '*'. The allowlist is supplied by the host application; this
//   module ships no hardcoded origins.

import { useRPilotConfig } from './useRPilotConfig.js'
import { useBridgeStore } from '../stores/bridge.js'
import { deriveSpaBase } from '../utils/spaBase.js'

const SOURCE_CLIENT_DEFAULT = 'rpilot'
const SOURCE_HOST_DEFAULT = 'host'

let installed = false
let activeConfig = null
const warnedKeys = new Set()
const pending = new Map()
const eventHandlers = new Map()

function warnOnce(key, message) {
  if (warnedKeys.has(key)) return
  warnedKeys.add(key)
  console.warn(`[rpilot bridge] ${message}`)
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function isAllowedOrigin(origin, cfg) {
  if (!origin) return false
  let url
  try { url = new URL(origin) } catch { return false }
  if (cfg.allowLocalhost !== false &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1')) return true
  for (const allow of cfg.allowedOrigins || []) {
    if (allow instanceof RegExp) { if (allow.test(origin)) return true; continue }
    if (typeof allow !== 'string') continue
    if (allow.startsWith('*.')) {
      if (url.hostname === allow.slice(2) || url.hostname.endsWith('.' + allow.slice(2))) return true
    } else if (allow === origin) {
      return true
    }
  }
  return false
}

function onMessage(event) {
  if (!activeConfig) return
  if (!isAllowedOrigin(event.origin, activeConfig)) return
  if (activeConfig.resolvedParentOrigin && event.origin !== activeConfig.resolvedParentOrigin) return
  const data = event.data
  if (!data || typeof data !== 'object') return
  if (data.source !== activeConfig.hostSource) return

  if (data.type === 'response' && data.id && pending.has(data.id)) {
    const { resolve, reject } = pending.get(data.id)
    pending.delete(data.id)
    if (data.error) {
      const err = new Error(data.error.message || 'Bridge error')
      err.code = data.error.code
      reject(err)
    } else {
      resolve(data.payload)
    }
    return
  }

  if (data.type === 'event') {
    eventHandlers.get(data.action)?.forEach(fn => fn(data.payload))
  }
}

function ensureInstalled() {
  if (installed || typeof window === 'undefined') return
  window.addEventListener('message', onMessage)
  installed = true
}

function inferParentOrigin(cfg) {
  if (cfg.parentOrigin && isAllowedOrigin(cfg.parentOrigin, cfg)) return cfg.parentOrigin
  const fromQuery = new URLSearchParams(window.location.search).get('parentOrigin')
  if (fromQuery && isAllowedOrigin(fromQuery, cfg)) return fromQuery
  if (document.referrer) {
    try {
      const origin = new URL(document.referrer).origin
      if (isAllowedOrigin(origin, cfg)) return origin
    } catch { /* invalid referrer */ }
  }
  return ''
}

function resolveBridgeConfig(rpcfg) {
  const b = rpcfg?.bridge
  if (!b) return null
  const enabled = b.enabled ?? 'auto'
  const inIframe = typeof window !== 'undefined' && window.self !== window.top
  const active = enabled === true || (enabled === 'auto' && inIframe)
  if (!active) return null
  return {
    clientSource: b.clientSource ?? SOURCE_CLIENT_DEFAULT,
    hostSource: b.hostSource ?? SOURCE_HOST_DEFAULT,
    allowedOrigins: b.allowedOrigins ?? [],
    allowLocalhost: b.allowLocalhost,
    parentOrigin: b.parentOrigin,
    navigateAction: b.navigateAction ?? 'snagr.navigate',
    readyAction: b.readyAction ?? 'rpilot.ready',
    setModeAction: b.setModeAction ?? 'snagr.rpilot.setMode',
    sessionChangedAction: b.sessionChangedAction ?? 'snagr.rpilot.sessionChanged',
    refreshTokenAction: b.refreshTokenAction ?? 'snagr.auth.getScopedToken',
    supportOpenChatAction: b.supportOpenChatAction ?? 'snagr.support.openChat',
    interceptLinks: b.interceptLinks ?? true,
    resolvedParentOrigin: '',
  }
}

const INACTIVE = Object.freeze({
  isActive: false,
  navigateAction: null,
  setModeAction: null,
  sessionChangedAction: null,
  refreshTokenAction: null,
  supportOpenChatAction: null,
  parentOrigin: '',
  call: () => Promise.reject(new Error('Parent bridge is not active')),
  on: () => () => {},
  emitReady: () => {},
  navigate: () => {},
})

export function useParentBridge() {
  const rpcfg = useRPilotConfig()
  const cfg = resolveBridgeConfig(rpcfg)
  if (!cfg) {
    // If the widget is in an iframe but nothing was configured, the host has
    // probably forgotten to pass a `bridge` block — surface that so they don't
    // wonder why links open new tabs and the expand button does nothing.
    if (typeof window !== 'undefined' && window.self !== window.top && !rpcfg?.bridge) {
      warnOnce(
        'no-bridge-config',
        'Widget is embedded but plugin config has no `bridge` block — parent bridge is inert. ' +
        'Pass `bridge: { enabled: "auto", allowedOrigins: [...] }` to RPilotChatPlugin to enable it.'
      )
    }
    return INACTIVE
  }

  ensureInstalled()

  // Reset cached state if the host source/client source changed since last use.
  if (!activeConfig ||
      activeConfig.clientSource !== cfg.clientSource ||
      activeConfig.hostSource !== cfg.hostSource) {
    activeConfig = cfg
  }
  if (!activeConfig.resolvedParentOrigin) {
    activeConfig.resolvedParentOrigin = inferParentOrigin(activeConfig)
  }

  function call(action, payload, { timeoutMs = 30000 } = {}) {
    if (window.parent === window) {
      return Promise.reject(new Error('Not embedded — no parent window'))
    }
    if (!activeConfig.resolvedParentOrigin) {
      warnOnce(
        'no-parent-origin',
        `Cannot deliver "${action}" — no parent origin resolved. ` +
        `document.referrer="${document.referrer || '(empty)'}" allowedOrigins=${JSON.stringify(activeConfig.allowedOrigins)}. ` +
        `Add the parent's origin to bridge.allowedOrigins (or RPILOT_ALLOWED_ORIGINS in the standalone). ` +
        `Calls will keep falling through to their default fallback (e.g. window.open for links) until this is fixed.`
      )
      return Promise.reject(new Error('Parent origin unknown'))
    }
    const id = uuid()
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(id)
        reject(new Error(`Bridge timeout for ${action}`))
      }, timeoutMs)
      pending.set(id, {
        resolve: v => { clearTimeout(timer); resolve(v) },
        reject: e => { clearTimeout(timer); reject(e) },
      })
      window.parent.postMessage(
        { source: activeConfig.clientSource, type: 'request', id, action, payload },
        activeConfig.resolvedParentOrigin,
      )
    })
  }

  function on(action, handler) {
    if (!eventHandlers.has(action)) eventHandlers.set(action, new Set())
    eventHandlers.get(action).add(handler)
    return () => eventHandlers.get(action)?.delete(handler)
  }

  function emitReady(payload = null) {
    if (window.parent === window || !activeConfig.resolvedParentOrigin) return
    window.parent.postMessage(
      { source: activeConfig.clientSource, type: 'event', action: activeConfig.readyAction, payload },
      activeConfig.resolvedParentOrigin,
    )
  }

  // Route a link click through the host SPA's router. The navigateAction
  // payload is the bare path (what `snagr.navigate` feeds to router.push).
  // Links on a foreign origin never belong to the host router — those open a
  // new tab directly, as does any bridge failure (timeout, no handler).
  //
  // "Internal" is judged against three origins, because they routinely differ:
  //   1. the widget's own origin (relative links),
  //   2. the parent shell's origin,
  //   3. the RDrive SPA base derived from the session's apiUrl — the origin the
  //      backend builds every deep/finder link against. The shell serves those
  //      routes even when it isn't hosted on that canonical domain (dev servers,
  //      alternate hostnames), so without this check every backend-generated
  //      link failed the origin test and opened a new tab instead of routing
  //      through the parent.
  function navigate(href) {
    if (!href) return
    let path = href
    let internal = true
    try {
      const url = new URL(href, window.location.origin)
      const spaBase = deriveSpaBase(useBridgeStore().apiUrl)
      internal = url.origin === window.location.origin ||
        (!!activeConfig.resolvedParentOrigin && url.origin === activeConfig.resolvedParentOrigin) ||
        (!!spaBase && url.origin === spaBase)
      path = url.pathname + url.search + url.hash
    } catch { /* unparseable — treat as a raw router path */ }
    if (!internal) {
      window.open(href, '_blank', 'noopener,noreferrer')
      return
    }
    call(cfg.navigateAction, path).catch(() => {
      window.open(href, '_blank', 'noopener,noreferrer')
    })
  }

  return {
    isActive: true,
    navigateAction: cfg.navigateAction,
    setModeAction: cfg.setModeAction,
    sessionChangedAction: cfg.sessionChangedAction,
    refreshTokenAction: cfg.refreshTokenAction,
    supportOpenChatAction: cfg.supportOpenChatAction,
    interceptLinks: cfg.interceptLinks,
    get parentOrigin() { return activeConfig.resolvedParentOrigin },
    call, on, emitReady, navigate,
  }
}

export function isEmbedded() {
  return typeof window !== 'undefined' && window.self !== window.top
}
